"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { GROQImage } from "@/sanity/lib/definitions";
import { PageTrademania2Schema, Trademania2Video } from "@/sanity/pages/PageTrademania2Template";
import { CountdownBox, FadeUp, Highlight, RevealWords, revealLines } from "@/components/trademania-2/shared";

import heroArc from "@/public/trademania-2/hero-arc.svg";

/**
 * Video frame shared by the hero and the last CTA: full-bleed on phones, inset card from lg up.
 * `phoneStrip`: on phones the video is a 16:9 strip at the top that fades into the dark section,
 * with the content stacked below it (as the WCE home hero).
 */
export function VideoFrame({
  video,
  poster,
  phoneStrip = false,
  className,
  children,
}: {
  video?: Trademania2Video;
  poster?: GROQImage;
  phoneStrip?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={cn("relative overflow-clip bg-black text-white lg:mx-[8px]", phoneStrip && "bg-[#10110E]", className)}>
      {video?.url && (
        <video
          className={
            phoneStrip
              ? "relative block aspect-video w-full object-cover lg:absolute lg:inset-0 lg:aspect-auto lg:h-full"
              : "absolute inset-0 h-full w-full object-cover"
          }
          src={video.url}
          poster={poster?.src}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
      )}
      <div
        className={cn(
          "absolute inset-0 bg-[rgba(0,0,0,.4)] bg-[linear-gradient(180deg,rgba(0,0,0,0)_25.2%,#10110E_65.8%)]",
          phoneStrip && "hidden lg:block",
        )}
      />
      {phoneStrip && (
        <div className="absolute inset-x-0 top-0 aspect-video bg-[linear-gradient(180deg,rgba(16,17,14,0)_40%,#10110E_100%)] lg:hidden" />
      )}
      {children}
    </section>
  );
}

export default function Hero({ data }: { data: PageTrademania2Schema }) {
  return (
    <VideoFrame
      video={data?.heroVideo}
      poster={data?.heroPoster}
      phoneStrip
      className="flex flex-col lg:mt-[6px] lg:h-[123rem] lg:flex-row lg:items-end lg:justify-center lg:rounded-t-[8px]">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={heroArc.src}
        alt=""
        aria-hidden
        className="pointer-events-none absolute top-[-2.13%] right-[-1.78%] bottom-[-2.07%] left-[-6.71%] hidden h-[104.2%] w-[108.49%] max-w-none lg:block"
      />

      <div className="relative z-[1] flex w-full flex-col items-center gap-[2.5rem] px-[2rem] pt-[2.5rem] pb-[3rem] lg:w-auto lg:gap-[3.5rem] lg:px-0 lg:pt-[16rem] lg:pb-[7.625rem]">
        <div className="flex w-full flex-col items-center gap-[2.5rem]">
          <div className="flex flex-col items-center gap-[1.75rem] lg:gap-[1.75rem]">
            <h1
              ref={revealLines}
              data-reveal
              className="text-center font-owners text-[5.5rem] leading-none font-medium tracking-[.01em] uppercase lg:text-[11rem] lg:leading-[.94]">
              <Highlight
                theme="dark"
                className="text-[#D9F9B9] italic">
                <RevealWords text={data?.heroTitleHighlight} />
              </Highlight>
              <br />
              <RevealWords
                text={data?.heroTitle}
                className="lg:text-[10.5rem]"
              />
            </h1>
            {data?.heroText && (
              <FadeUp className="w-full lg:w-[95.75rem]">
                <p className="text-center font-geist text-[2.5rem] leading-[1.4] font-medium text-white/80 lg:leading-[1.5]">
                  {data.heroText}
                </p>
              </FadeUp>
            )}
          </div>
          {data?.heroCTA && (
            <FadeUp
              className="w-full lg:w-auto"
              delay={0.1}>
              <CTACornerButton data={data.heroCTA} />
            </FadeUp>
          )}
        </div>
        <FadeUp
          className="flex w-full justify-center"
          delay={0.2}>
          <CountdownBox
            label={data?.eventDateLabel}
            target={data?.eventDate}
          />
        </FadeUp>
      </div>
    </VideoFrame>
  );
}
